import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from '@/components/ui/form';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { segmentService, profileService, userAdminService } from '@/services';
import type { ProjectSegmentWithRelations } from '@/services';

const segmentFormSchema = z.object({
  segment_id: z.string().min(1, 'Segment is required'),
  segment_manager: z.string().nullable().optional(),
  notes: z.string().optional(),
});

type SegmentFormValues = z.infer<typeof segmentFormSchema>;

interface ProjectSegmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: SegmentFormValues) => Promise<void>;
  editingSegment?: ProjectSegmentWithRelations | null;
}

const defaultValues: SegmentFormValues = {
  segment_id: '',
  segment_manager: null,
  notes: '',
};

export default function ProjectSegmentFormDialog({ open, onOpenChange, onSave, editingSegment }: ProjectSegmentFormDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [saveAction, setSaveAction] = useState<'close' | 'new'>('close');
  const { user } = useAuth();

  const form = useForm<SegmentFormValues>({
    resolver: zodResolver(segmentFormSchema),
    defaultValues,
  });

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const { data: segments = [] } = useQuery({
    queryKey: ['list-segments-active', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await segmentService.listActive(profile.company_id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!profile?.company_id,
  });

  const { data: managers = [] } = useQuery({
    queryKey: ['company-profiles', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await userAdminService.listUsers(profile.company_id);
      if (result.error) throw result.error;
      return [...result.data].sort((a, b) =>
        (a.full_name ?? '').localeCompare(b.full_name ?? ''),
      );
    },
    enabled: open && !!profile?.company_id,
  });

  useEffect(() => {
    if (open) {
      if (editingSegment) {
        form.reset({
          segment_id: editingSegment.segment_id,
          segment_manager: editingSegment.segment_manager || null,
          notes: editingSegment.notes || '',
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, editingSegment, form]);

  const handleSubmit = async (data: SegmentFormValues) => {
    setIsLoading(true);
    try {
      await onSave(data);
      if (saveAction === 'new') {
        form.reset(defaultValues);
      } else {
        onOpenChange(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isEditing = !!editingSegment;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Segment' : 'Add Segment'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="segment_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Segment *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a segment" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {segments.filter(s => s.id).map((segment) => (
                        <SelectItem key={segment.id} value={segment.id}>
                          {segment.display_value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="segment_manager"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Segment Manager</FormLabel>
                  <Select
                    value={field.value || '__none__'}
                    onValueChange={(value) => field.onChange(value === '__none__' ? null : value)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a manager" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {managers.filter(m => m.id).map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.full_name || 'Unnamed'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {!isEditing && (
                <Button
                  type="submit"
                  variant="outline"
                  disabled={isLoading}
                  onClick={() => setSaveAction('new')}
                >
                  {isLoading && saveAction === 'new' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save & New'
                  )}
                </Button>
              )}
              <Button
                type="submit"
                disabled={isLoading}
                onClick={() => setSaveAction('close')}
              >
                {isLoading && saveAction === 'close' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save & Close'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
